import { loadStripe, type Stripe, type PaymentRequest } from '@stripe/stripe-js';
import { type ComputedRef } from 'vue';

export function usePaymentRequest(eventName: ComputedRef<string>, totalAmount: ComputedRef<number>, isEnablePaymentRequest: ComputedRef<boolean>) {
	const checkoutStore = useCheckoutStore();
	const eventStore = useEventStore();

	const stripeWalletInstance = ref<Stripe | null>(null);
	const stripeCreditCardInstance = ref<Stripe | null>(null);
	const paymentRequestInstance = ref<PaymentRequest | null>();
	const isGooglePayAvailable = ref(false);
	const isApplePayAvailable = ref(false);
	const isVerifyingAvailablePaymentOption = ref(false);

	const formattedTotalAmount = computed(() => {
		return checkoutStore.paymentMethodMetaData.isSmallestUnit ? totalAmount.value : toCents(totalAmount.value);
	});

	async function initStripeInstances() {
		const publishKey = checkoutStore.stripePublishableKey;
		if (!publishKey) {
			throw new TypeError('Stripe public key is not defined');
		}

		if (eventStore.isUSEvent && !checkoutStore.stripeAccountId) {
			throw new TypeError('Stripe account id is not defined');
		}

		isVerifyingAvailablePaymentOption.value = true;

		// Init payment instance for wallet option (Google Pay and Apple Pay)
		stripeWalletInstance.value = await loadStripe(publishKey);

		// Init payment instance for credit card option (with Stripe Connect for USA)
		stripeCreditCardInstance.value = await loadStripe(publishKey, (eventStore.isUSEvent ? {stripeAccount: checkoutStore.stripeAccountId || undefined} : {}));

		if (!stripeWalletInstance.value || !stripeCreditCardInstance.value) {
			isVerifyingAvailablePaymentOption.value = false;
			return;
		}

		paymentRequestInstance.value = stripeWalletInstance.value.paymentRequest({
			country: checkoutStore.paymentMethodMetaData.country,
			currency: checkoutStore.paymentMethodMetaData.currency,
			total: {
				label: eventName.value,
				amount: formattedTotalAmount.value,
			},
			requestPayerName: true,
			requestPayerEmail: true,
		});

		// Check the availability of the Payment Request API first.
		const result = await paymentRequestInstance.value.canMakePayment();
		if (!result) {
			isVerifyingAvailablePaymentOption.value = false;
			return;
		}
		isApplePayAvailable.value = result.applePay;
		isGooglePayAvailable.value = result.googlePay;
		isVerifyingAvailablePaymentOption.value = false;
	}

	function resetState() {
		stripeWalletInstance.value = null;
		stripeCreditCardInstance.value = null;
		paymentRequestInstance.value = null;
		isGooglePayAvailable.value = false;
		isApplePayAvailable.value = false;
		isVerifyingAvailablePaymentOption.value = false;
	}

	watch([isEnablePaymentRequest, totalAmount], ([newIsEnablePaymentRequest, newTotalAmount]) => {
		if (newIsEnablePaymentRequest && newTotalAmount > 0) {
			initStripeInstances();
		} else if (!newIsEnablePaymentRequest && stripeWalletInstance.value) {
			resetState();
		}
	});

	return {
		isGooglePayAvailable,
		isApplePayAvailable,
		isVerifyingAvailablePaymentOption,
		paymentRequestInstance,
		stripeCreditCardInstance,
		stripeWalletInstance,
	};
}
